/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import type { MyHubInfoCardProps } from "./MyHubInfoCardProps";
import "./MyHubMovieInfoCard.scss";

/**
 * MyHubMovieInfoCard — the recording highlight's text block for a movie
 * (Figma: Unity Design System › InfoCards › Type=Movie, node 1961:24411).
 *
 * The title, then "2h 6m • PG-13", then the airing line ("Today  9:00-10:00pm
 * on NBC") over "Expires on …". Each row drops out when the item carries
 * nothing for it. The sheet around it — backdrop and action row — is
 * MyHubRecordingHighlight's.
 */
const MyHubMovieInfoCard: FunctionComponent<MyHubInfoCardProps> = (
  props: MyHubInfoCardProps
): JSX.Element => {
  const { item } = props;
  const ratingBadge: string = item.tile.ratingBadge || "";
  const hasRuntimeRow: boolean =
    item.durationLabel !== "" || ratingBadge !== "";
  const hasSummary: boolean =
    item.scheduleLabel !== "" || item.expiresOnLabel !== "";

  return (
    <div
      className="my-hub-movie-info-card"
      data-testid="my-hub-movie-info-card"
    >
      <div className="my-hub-movie-info-card__metadata">
        <h2 className="my-hub-movie-info-card__title">{item.title}</h2>
        {hasRuntimeRow && (
          <div className="my-hub-movie-info-card__runtime-row">
            {item.durationLabel !== "" && (
              <span className="my-hub-movie-info-card__runtime">
                {item.durationLabel}
              </span>
            )}
            {item.durationLabel !== "" && ratingBadge !== "" && (
              <span
                className="my-hub-movie-info-card__dot"
                aria-hidden="true"
              />
            )}
            {ratingBadge !== "" && (
              <span className="my-hub-recording-highlight__rating-badge">
                {ratingBadge}
              </span>
            )}
          </div>
        )}
      </div>

      {hasSummary && (
        <div className="my-hub-movie-info-card__summary">
          {item.scheduleLabel !== "" && (
            <p className="my-hub-movie-info-card__schedule">
              {item.scheduleLabel}
            </p>
          )}
          {item.expiresOnLabel !== "" && (
            <p className="my-hub-recording-highlight__expires">
              {item.expiresOnLabel}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default MyHubMovieInfoCard;
